import { initWebTelemetry } from '@athwab/observability/web';
import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './app/App';
import { Toaster } from './components/ui/sonner';
import { initProductAnalytics } from './lib/analytics';
import './styles/globals.css';
import { initI18n } from './i18n/config';

initWebTelemetry();
initProductAnalytics();

declare global {
  interface Window {
    __athwabTelemetryDebug?: Record<string, unknown>;
    __athwabCaptureException?: (error?: unknown) => void;
  }
}

const sentryState = window.__SENTRY__;

window.__athwabTelemetryDebug = window.__athwabTelemetryDebug ?? {
  dsn: import.meta.env.VITE_SENTRY_DSN ?? import.meta.env.SENTRY_DSN ?? null,
  release: import.meta.env.VITE_APP_RELEASE ?? import.meta.env.SENTRY_RELEASE ?? null,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE ?? null,
  initialized: Boolean(sentryState?.hub)
};

window.__athwabCaptureException = (error?: unknown) => {
  import('@sentry/react').then((Sentry) => {
    const value = error instanceof Error ? error : new Error('web smoke test');
    const eventId = Sentry.captureException(value);
    window.__athwabTelemetryDebug = {
      ...(window.__athwabTelemetryDebug ?? {}),
      lastEventId: eventId,
      initialized: true
    };
    console.debug('[observability] sentry web smoke test', { eventId });
  }).catch((err) => {
    console.error('[observability] failed to capture Sentry exception', err);
  });
};

const bootstrap = async () => {
  const rootElement = document.getElementById('root');

  if (!rootElement) {
    throw new Error('Root element not found');
  }

  // Resources are bundled locally, so we can initialize i18n without blocking the first paint.
  void initI18n();

  createRoot(rootElement).render(
    <React.StrictMode>
      <App />
      <Toaster />
    </React.StrictMode>
  );
};

void bootstrap();
